import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useApp } from '../hooks/useApp'
import { supabase } from '../lib/supabase'
import { careCopy } from '../config/careCopy'
import { carePushCopy } from '../config/carePushCopy'
import CareAlerts from '../components/CareAlerts'
import CareConnect from '../components/CareConnect'
import { carePushRequest } from '../lib/carePush'
import { careReactions } from '../config/careReactions'
import { careAlertTarget } from '../lib/careAlertTarget'

function SupportFrame({ children }) {
  const navigate = useNavigate()
  return <div className="page">
    <div className="status-bar">
      <button className="btn-ghost" onClick={() => navigate('/')}>Back</button>
      <a href="/" style={{ color: 'inherit', padding: '12px 0' }} aria-label="Home">Regulated</a>
    </div>
    <main className="page-content-wide care-content" style={{ paddingTop: 8, paddingBottom: 120 }}>
      <h1 style={{ margin: '0 0 20px', font: '300 32px/38px var(--font-display)' }}>{careCopy.pageTitle}</h1>
      {children}
    </main>
  </div>
}

function SupportSpace({ link, userId, alertTarget }) {
  const isPractitioner = userId === link.practitioner_id
  const otherName = isPractitioner ? link.client_label : link.practitioner_label
  const [section, setSection] = useState('tasks')
  const [tasks, setTasks] = useState([])
  const [entries, setEntries] = useState([])
  const [messages, setMessages] = useState([])
  const [reactions, setReactions] = useState([])
  const [reactionsReady, setReactionsReady] = useState(false)
  const [status, setStatus] = useState('loading')
  const [refreshVersion, setRefreshVersion] = useState(0)
  const [error, setError] = useState(false)
  const [saving, setSaving] = useState(false)
  const [taskTitle, setTaskTitle] = useState('')
  const [instructions, setInstructions] = useState('')
  const [notes, setNotes] = useState({})
  const [messageText, setMessageText] = useState('')
  const [highlighted, setHighlighted] = useState(null)
  const shownAlert = useRef(null)
  const highlightTimer = useRef(null)
  const taskRequest = useRef(0)
  const messageRequest = useRef(0)

  const loadTasks = useCallback(async () => {
    const request = ++taskRequest.current
    const { data: taskRows, error: taskError } = await supabase.from('care_tasks')
      .select('id,title,instructions,created_at')
      .eq('client_id', link.client_id).eq('practitioner_id', link.practitioner_id)
      .order('created_at', { ascending: false })
    if (request !== taskRequest.current) return false
    if (taskError) throw taskError
    const ids = (taskRows || []).map(row => row.id)
    const result = ids.length
      ? await supabase.from('care_task_entries').select('id,task_id,entry_type,body,created_at')
          .in('task_id', ids).order('created_at')
      : { data: [], error: null }
    if (request !== taskRequest.current) return false
    if (result.error) throw result.error
    setTasks(taskRows || [])
    setEntries(result.data || [])
    return true
  }, [link.client_id, link.practitioner_id])

  const loadMessages = useCallback(async () => {
    const request = ++messageRequest.current
    // ponytail: load the full thread for now; paginate when conversations grow.
    const { data, error: readError } = await supabase.from('care_messages')
      .select('id,sender_id,body,created_at')
      .eq('client_id', link.client_id).eq('practitioner_id', link.practitioner_id)
      .order('created_at')
    if (request !== messageRequest.current) return false
    if (readError) throw readError
    setMessages(data || [])
    const ids = (data || []).map(message => message.id)
    const result = ids.length
      ? await supabase.from('care_message_reactions').select('message_id,user_id,emoji').in('message_id', ids)
      : { data: [], error: null }
    if (request !== messageRequest.current) return false
    // Existing conversations remain usable during schema rollout or reaction outages.
    setReactionsReady(!result.error)
    setReactions(result.error ? [] : result.data || [])
    return true
  }, [link.client_id, link.practitioner_id])

  const loadSection = useCallback(() => section === 'tasks' ? loadTasks() : loadMessages(),
    [section, loadTasks, loadMessages])

  useEffect(() => {
    if (alertTarget.section) setSection(alertTarget.section)
  }, [alertTarget.key, alertTarget.section])

  useEffect(() => {
    let active = true
    setStatus('loading')
    setError(false)
    loadSection()
      .then(loaded => { if (active && loaded) setStatus('ready') })
      .catch(() => { if (active) setStatus('error') })
    const poll = setInterval(() => {
      loadSection()
        .then(loaded => { if (active && loaded) { setStatus('ready'); setError(false) } })
        .catch(() => { if (active) setError(true) })
      carePushRequest('dispatch').catch(() => {})
    }, 15000)
    return () => {
      active = false
      taskRequest.current += 1
      messageRequest.current += 1
      clearInterval(poll)
    }
  }, [loadSection, alertTarget.key, refreshVersion])

  useEffect(() => {
    if (status !== 'ready' || !alertTarget.section || section !== alertTarget.section || shownAlert.current === alertTarget.key) return
    const rows = section === 'tasks' ? tasks : messages
    const entry = section === 'tasks' ? entries.find(item => item.id === alertTarget.item) : null
    const row = alertTarget.item ? rows.find(item => item.id === (entry?.task_id || alertTarget.item))
      : section === 'tasks' ? rows[0] : rows.at(-1)
    if (!row) return
    const element = document.getElementById(entry?.entry_type === 'note' ? `care-entry-${entry.id}` : `care-${section}-${row.id}`)
    if (!element) return
    shownAlert.current = alertTarget.key
    setHighlighted(row.id)
    element.focus({ preventScroll: true })
    element.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth', block: 'center' })
    clearTimeout(highlightTimer.current)
    highlightTimer.current = setTimeout(() => setHighlighted(null), 12000)
  }, [alertTarget, section, status, tasks, entries, messages])

  useEffect(() => () => clearTimeout(highlightTimer.current), [])

  async function assignTask(event) {
    event.preventDefault()
    if (!taskTitle.trim() || saving) return
    setSaving(true)
    setError(false)
    try {
      const { error: writeError } = await supabase.from('care_tasks').insert({
        client_id: link.client_id, practitioner_id: link.practitioner_id,
        title: taskTitle.trim(), instructions: instructions.trim(),
      })
      if (writeError) throw writeError
      await carePushRequest('dispatch').catch(() => {})
      setTaskTitle('')
      setInstructions('')
      await loadTasks()
    } catch { setError(true) }
    finally { setSaving(false) }
  }

  async function addEntry(taskId, entryType) {
    if (saving || (entryType === 'note' && !notes[taskId]?.trim())) return
    setSaving(true)
    setError(false)
    try {
      const { error: writeError } = await supabase.from('care_task_entries').insert({
        task_id: taskId, entry_type: entryType,
        body: entryType === 'note' ? notes[taskId].trim() : '',
      })
      if (writeError) throw writeError
      setNotes(current => ({ ...current, [taskId]: '' }))
      await carePushRequest('dispatch').catch(() => {})
      await loadTasks()
    } catch { setError(true) }
    finally { setSaving(false) }
  }

  async function sendMessage(event) {
    event.preventDefault()
    if (!messageText.trim() || saving) return
    setSaving(true)
    setError(false)
    try {
      const { error: writeError } = await supabase.from('care_messages').insert({
        client_id: link.client_id, practitioner_id: link.practitioner_id,
        sender_id: userId, body: messageText.trim(),
      })
      if (writeError) throw writeError
      setMessageText('')
      await carePushRequest('dispatch').catch(() => {})
      await loadMessages()
    } catch { setError(true) }
    finally { setSaving(false) }
  }

  async function react(messageId, emoji) {
    if (saving) return
    setSaving(true)
    setError(false)
    try {
      const current = reactions.find(row => row.message_id === messageId && row.user_id === userId)
      const result = current?.emoji === emoji
        ? await supabase.from('care_message_reactions').delete().eq('message_id', messageId).eq('user_id', userId)
        : await supabase.from('care_message_reactions').upsert(
            { message_id: messageId, user_id: userId, emoji }, { onConflict: 'message_id,user_id' })
      if (result.error) throw result.error
      await loadMessages()
    } catch { setError(true) }
    finally { setSaving(false) }
  }

  return <>
    <p className={`care-role${isPractitioner ? ' care-role-practitioner' : ''}`} role="status">{isPractitioner ? careCopy.practitionerRole : careCopy.clientRole}</p>
    <h2 style={{ font: '400 22px/28px var(--font-display)' }}>{otherName}</h2>
    <button className="btn-ghost" disabled={saving}
      onClick={() => setRefreshVersion(value => value + 1)}>{carePushCopy.refresh}</button>
    <div role="tablist" aria-label={careCopy.pageTitle} className="segmented" style={{ margin: '18px 0' }}>
      {['tasks', 'messages'].map(value => <button key={value} className="segmented-item" role="tab"
        id={`care-${value}-tab`} aria-controls={`care-${value}-panel`}
        aria-selected={section === value} onClick={() => setSection(value)}>
        {value === 'tasks' ? (isPractitioner ? careCopy.practitionerTasks : careCopy.clientTasks) : careCopy.messagesTitle}
      </button>)}
    </div>
    {status === 'loading' && <p role="status">{careCopy.loading}</p>}
    {status === 'error' && <p role="alert">{careCopy.error}</p>}
    {error && <p role="alert">{careCopy.error}</p>}
    {status === 'ready' && section === 'tasks' && <div role="tabpanel" id="care-tasks-panel" aria-labelledby="care-tasks-tab">
      <p>{isPractitioner ? careCopy.practitionerHelp : careCopy.clientHelp}</p>
      {isPractitioner && <details className="card" style={{ marginBottom: 18 }}>
        <summary style={{ cursor: 'pointer', fontWeight: 600 }}>{careCopy.assignTitle}</summary>
        <form onSubmit={assignTask}>
        <label htmlFor="care-task-title">{careCopy.taskTitleLabel}</label>
        <input className="form-input" id="care-task-title" value={taskTitle} onChange={event => setTaskTitle(event.target.value)}
          maxLength={160} required style={{ display: 'block', width: '100%', margin: '8px 0 16px' }} />
        <label htmlFor="care-task-instructions">{careCopy.taskInstructionsLabel}</label>
        <textarea className="form-input form-textarea" id="care-task-instructions" value={instructions}
          onChange={event => setInstructions(event.target.value)} maxLength={4000}
          style={{ display: 'block', width: '100%', minHeight: 90, margin: '8px 0 16px' }} />
        <button className="btn-primary" type="submit" disabled={saving}>{careCopy.assignButton}</button>
        </form>
      </details>}
      {tasks.length === 0 && <p>{careCopy.emptyTasks}</p>}
      {tasks.map(task => {
        const taskEntries = entries.filter(entry => entry.task_id === task.id)
        const complete = taskEntries.some(entry => entry.entry_type === 'complete')
        return <article key={task.id} id={`care-tasks-${task.id}`} tabIndex={-1}
          className={`card${highlighted === task.id ? ' care-alert-target' : ''}`} style={{ marginBottom: 14 }}>
          <h3 style={{ marginTop: 0 }}>{task.title}</h3>
          {task.instructions && <p style={{ whiteSpace: 'pre-wrap' }}>{task.instructions}</p>}
          {complete && <p role="status">{careCopy.completionLabel}</p>}
          {taskEntries.filter(entry => entry.entry_type === 'note').map(entry =>
            <p key={entry.id} id={`care-entry-${entry.id}`} tabIndex={-1}
              style={{ whiteSpace: 'pre-wrap', borderTop: '1px solid var(--line)', paddingTop: 10 }}>
              <small>{new Date(entry.created_at).toLocaleString()}</small><br />{entry.body}
            </p>)}
          {!isPractitioner && <>
            <label htmlFor={`note-${task.id}`}>{careCopy.noteLabel}</label>
            <textarea className="form-input form-textarea" id={`note-${task.id}`} value={notes[task.id] || ''}
              onChange={event => setNotes(current => ({ ...current, [task.id]: event.target.value }))}
              maxLength={5000} style={{ display: 'block', width: '100%', minHeight: 80, margin: '8px 0' }} />
            <button className="btn-primary" onClick={() => addEntry(task.id, 'note')} disabled={saving || !notes[task.id]?.trim()}>
              {careCopy.saveNote}
            </button>
            {!complete && <button className="btn-ghost" onClick={() => addEntry(task.id, 'complete')} disabled={saving}>
              {careCopy.completeTask}
            </button>}
          </>}
        </article>
      })}
    </div>}
    {status === 'ready' && section === 'messages' && <div role="tabpanel" id="care-messages-panel" aria-labelledby="care-messages-tab">
      <p style={{ fontSize: 14 }}>{careCopy.urgentNotice}</p>
      {messages.length === 0 && <p>{careCopy.emptyMessages}</p>}
      {messages.map(message => <div key={message.id} id={`care-messages-${message.id}`} tabIndex={-1}
        className={`card${highlighted === message.id ? ' care-alert-target' : ''}`}
        style={{ margin: '10px 0', maxWidth: '90%', marginLeft: message.sender_id === userId ? 'auto' : 0 }}>
        <small>{message.sender_id === userId ? 'You' : otherName} · {new Date(message.created_at).toLocaleString()}</small>
        <p style={{ whiteSpace: 'pre-wrap', marginBottom: 0 }}>{message.body}</p>
        {reactionsReady && <div className="care-reactions">
          {careReactions.map(({ emoji, label }) => {
            const rows = reactions.filter(row => row.message_id === message.id && row.emoji === emoji)
            const selected = rows.some(row => row.user_id === userId)
            return <button key={emoji} type="button" aria-label={label} aria-pressed={selected}
              disabled={saving} onClick={() => react(message.id, emoji)}>
              <span aria-hidden="true">{emoji}</span>{rows.length > 0 && <span>{rows.length}</span>}
            </button>
          })}
        </div>}
      </div>)}
      <form onSubmit={sendMessage} style={{ marginTop: 18 }}>
        <label htmlFor="care-message">{careCopy.messageLabel}</label>
        <textarea className="form-input form-textarea" id="care-message" value={messageText} onChange={event => setMessageText(event.target.value)}
          maxLength={5000} required style={{ display: 'block', width: '100%', minHeight: 90, margin: '8px 0 14px' }} />
        <button className="btn-primary" type="submit" disabled={saving}>{careCopy.sendMessage}</button>
      </form>
    </div>}
  </>
}

export default function Care() {
  const navigate = useNavigate()
  const location = useLocation()
  const alertTarget = useMemo(() => ({ ...careAlertTarget(location.search), key: location.key }), [location.search, location.key])
  const { authUser, authReady } = useApp()
  const [result, setResult] = useState({ status: 'loading', rows: [], userId: null })
  const [selectedPair, setSelectedPair] = useState(null)
  const [managerId, setManagerId] = useState(null)
  const [reloadVersion, setReloadVersion] = useState(0)


  useEffect(() => {
    if (!authReady) return
    if (!authUser) {
      setResult({ status: 'sign_in', rows: [], userId: null })
      return
    }
    let active = true
    setResult({ status: 'loading', rows: [], userId: authUser.id })
    supabase.from('care_links').select('client_id,practitioner_id,client_label,practitioner_label')
      .order('created_at').then(({ data, error }) => {
        if (!active) return
        setResult(error
          ? { status: 'error', rows: [], userId: authUser.id }
          : { status: 'ready', rows: data || [], userId: authUser.id })
      }).catch(() => {
        if (active) setResult({ status: 'error', rows: [], userId: authUser.id })
      })
    return () => { active = false }
  }, [authReady, authUser?.id, reloadVersion])

  const status = !authReady ? 'loading' : !authUser ? 'sign_in'
    : result.userId === authUser.id ? result.status : 'loading'
  const pairKey = link => `${link.client_id}:${link.practitioner_id}`
  const isPractitioner = managerId === authUser?.id || result.rows.some(link => link.practitioner_id === authUser?.id)
  const selected = result.rows.find(link => pairKey(link) === (alertTarget.pair || selectedPair))
    || (!isPractitioner ? result.rows[0] : null)

  return <SupportFrame>
    {status === 'loading' && <p role="status">{careCopy.loading}</p>}
    {status === 'sign_in' && <>
      <p>{careCopy.signInPrompt}</p>
      <button className="btn-primary btn-lg" onClick={() => navigate('/signin?next=/care')}>Sign in</button>
    </>}
    {status === 'error' && <>
      <p role="alert">{careCopy.error}</p>
      <button className="btn-primary" onClick={() => setReloadVersion(value => value + 1)}>{carePushCopy.refresh}</button>
    </>}
    {status === 'ready' && <CareAlerts key={authUser.id} userId={authUser.id} />}
    {status === 'ready' && isPractitioner && !selected && <>
      <h2 style={{ font: '400 22px/28px var(--font-display)' }}>Connected clients</h2>
      {result.rows.length === 0 && <p>No connected clients yet.</p>}
    </>}
    {status === 'ready' && !isPractitioner && result.rows.length === 0 && <p>{carePushCopy.emptySpace}</p>}
    {status === 'ready' && ((isPractitioner && !selected) || (!isPractitioner && result.rows.length > 1)) && <div
      aria-label={isPractitioner ? 'Connected clients' : 'Connections'}
      style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {result.rows.map(link => <button key={pairKey(link)} className="card care-client-link"
        aria-current={selected === link ? 'true' : undefined}
        onClick={() => { navigate('/care', { replace: true }); setSelectedPair(pairKey(link)) }}>
        {authUser.id === link.practitioner_id ? link.client_label : link.practitioner_label}
      </button>)}
    </div>}
    {status === 'ready' && <CareConnect key={`connect-${authUser.id}`} userId={authUser.id} showForm={!selected}
      onCanConnect={setManagerId} onConnected={() => setReloadVersion(value => value + 1)} />}
    {status === 'ready' && isPractitioner && selected && <button className="btn-ghost" onClick={() => {
      navigate('/care', { replace: true }); setSelectedPair(null)
    }}>Connected clients</button>}
    {status === 'ready' && selected && <SupportSpace key={pairKey(selected)} link={selected} userId={authUser.id} alertTarget={alertTarget} />}
  </SupportFrame>
}
